/**
 * Flow Connection Management Composable
 *
 * Handles the calculation and rendering of visual connections between flow nodes.
 * This composable:
 * - Calculates node positions in the transformed canvas space
 * - Determines connection paths between nodes
 * - Handles both regular connections and split node connections
 * - Accounts for zoom/pan transformations
 *
 * The connection system works by:
 * 1. Querying the DOM for node elements
 * 2. Calculating their positions relative to the container
 * 3. Accounting for CSS transforms (scale, translate)
 * 4. Creating connection objects with path data
 *
 * This approach allows connections to update automatically when
 * nodes move or the canvas is transformed.
 */

import { ref, Ref } from 'vue'
import { FlowConnection } from '../types'

export function useFlow(
  flowBuilder: Ref<HTMLElement | null>,
  container: Ref<HTMLElement | null>
) {
  const edges = ref<any[]>([])
  const connections = ref<FlowConnection[]>([])
  const nodeElementPositions = ref<
    Map<string, { x: number; y: number; width: number; height: number }>
  >(new Map())
  const heatMapNodePositions = ref<
    Map<string, { x: number; y: number; width: number; height: number }>
  >(new Map())

  /**
   * Main connection drawing function
   * Called whenever nodes move or canvas transforms change
   */
  const drawConnections = (styleVariables: Record<string, any>) => {
    // Skip drawing during SSR or if browser APIs aren't available
    if (typeof window === 'undefined' || !container.value) {
      connections.value = []
      return
    }

    const trueScale = styleVariables['--scale'] || 1
    const connectionPaths: FlowConnection[] = []
    const nodePositions = new Map<
      string,
      { x: number; y: number; width: number; height: number }
    >()
    const heatMapPositions = new Map<
      string,
      { x: number; y: number; width: number; height: number }
    >()

    // Hoist getComputedStyle + DOMMatrix to top of drawConnections —
    // these are the same for every node/connection in a single draw call.
    const containerStyle = window.getComputedStyle(container.value)
    const matrix = new DOMMatrix(containerStyle.transform)
    const containerRect = container.value.getBoundingClientRect()
    const matrixScale = matrix.a
    const matrixOffsetX = matrix.e
    const matrixOffsetY = matrix.f

    /**
     * Calculate element layout in canvas space
     *
     * This function is crucial for accurate connection positioning.
     * It converts element positions from screen space to canvas space,
     * accounting for:
     * - Container position
     * - CSS transform scale
     * - CSS transform translate (pan)
     *
     * Without this conversion, connections would appear offset
     * when the canvas is zoomed or panned.
     */
    const calculateLayout = (el: HTMLElement) => {
      if (!el || !container.value) {
        return { x: 0, y: 0, width: 0, height: 0, centerX: 0, centerY: 0 }
      }

      const rect = el.getBoundingClientRect()

      // Calculate the scale and offset from the hoisted transform matrix
      const scale = trueScale
      const offsetX = matrixOffsetX
      const offsetY = matrixOffsetY

      // Convert screen coordinates to canvas coordinates
      // This reverses the transform to get the original position
      const x = (rect.left - containerRect.left) / scale - offsetX / scale
      const y = (rect.top - containerRect.top) / scale - offsetY / scale
      const width = rect.width / scale
      const height = rect.height / scale

      const layout = {
        x,
        y,
        width,
        height,
        centerX: x + width / 2,
        centerY: y + height / 2
      }

      return layout
    }

    /**
     * Add a connection between two points
     *
     * Transforms the connection points from canvas space to screen space
     * by applying the current transform matrix. This ensures connections
     * appear in the correct position regardless of zoom/pan state.
     */
    const addConnection = ({
      from,
      to,
      type
    }: {
      from: any
      to: any
      type?: string
    }) => {
      if (!container.value) return

      // Use hoisted matrix values instead of re-computing per connection
      const scale = matrixScale
      const offsetX = matrixOffsetX
      const offsetY = matrixOffsetY

      // Transform points from canvas space to screen space
      const transformPoint = (point: { x: number; y: number }) => ({
        x: point.x * scale + offsetX,
        y: point.y * scale + offsetY
      })

      const transformedFrom = transformPoint(from)
      const transformedTo = transformPoint(to)

      const path = [
        { x: transformedFrom.x, y: transformedFrom.y, el: from.el },
        { x: transformedTo.x, y: transformedTo.y, el: to.el }
      ]
      // Use deterministic IDs so Vue can diff connections instead of destroy/recreate
      const connection: FlowConnection = {
        id: `${transformedFrom.x.toFixed(0)}-${transformedFrom.y.toFixed(0)}-${transformedTo.x.toFixed(0)}-${transformedTo.y.toFixed(0)}`,
        path,
        type // 'linear' for splits, undefined for regular connections
      }
      connectionPaths.push(connection)
    }

    /**
     * Check if a split column allows flow-through (doesn't terminate).
     *
     * Returns true if the branch flows through and needs a convergence line.
     * Returns false if the branch terminates (all paths end).
     *
     * Uses the data-branch-terminates attribute set by NodeRun, which
     * correctly uses the recursive doesNodeTerminate() function.
     * This avoids the bug where querySelectorAll('.does-terminate') would
     * find terminal nodes deep inside nested branches (e.g., an Offer
     * inside a YesNo's "Yes" path) and incorrectly mark the whole column
     * as terminated.
     */
    const splitFlowsThrough = (node: Element) => {
      if (node.classList.contains('flowbuilder-split')) {
        const runTrack = node.querySelector(':scope > .run-track')
        if (!runTrack) return true // empty branch → flows through
        return runTrack.getAttribute('data-branch-terminates') !== 'true'
      }

      return true
    }

    const el = container.value
    if (!el) {
      connections.value = []
      return
    }

    let terminated = false

    const widgetsLayout = calculateLayout(
      el.querySelector('.flowbuilder-widgets') as HTMLElement
    )
    el.querySelectorAll('.flowbuilder-widgets .flowbuilder-node').forEach(
      (widget) => {
        const widgetLayout = calculateLayout(widget as HTMLElement)

        // Track widget position
        const widgetElement = widget as HTMLElement
        const widgetId =
          widgetElement.getAttribute('data-node-id') ||
          `widget-${Math.random()}`
        nodePositions.set(widgetId, {
          x: widgetLayout.x,
          y: widgetLayout.y,
          width: widgetLayout.width,
          height: widgetLayout.height
        })

        // Also track widget positions for heat map (widgets are already .flowbuilder-node elements)
        heatMapPositions.set(widgetId, {
          x: widgetLayout.x,
          y: widgetLayout.y,
          width: widgetLayout.width,
          height: widgetLayout.height
        })

        addConnection({
          from: {
            x: widgetLayout.centerX,
            y: widgetLayout.y + widgetLayout.height
          },
          to: {
            x: widgetsLayout.centerX,
            y: widgetsLayout.y + widgetsLayout.height
          },
          type: 'curve-force'
        })
      }
    )

    el.querySelectorAll('.run-track').forEach((runTrack) => {
      const trackComponents = Array.from(runTrack.children).filter((e) =>
        e.classList.contains('run-track-item')
      ) as HTMLElement[]

      trackComponents.forEach((runTrackComponent) => {
        const runTrackComponentLayout = calculateLayout(runTrackComponent)
        const node = runTrackComponent.querySelector<HTMLElement>(
          '.flowbuilder-node-component'
        )

        if (node && node.querySelectorAll('.does-terminate').length !== 0) {
          terminated = false
        }

        if (node && !terminated) {
          const nodeLayout = calculateLayout(node)

          // Track node position by data-node-id
          const nodeElement = node.querySelector('[data-node-id]')
          if (nodeElement) {
            const nodeId = nodeElement.getAttribute('data-node-id')
            if (nodeId) {
              nodePositions.set(nodeId, {
                x: nodeLayout.x,
                y: nodeLayout.y,
                width: nodeLayout.width,
                height: nodeLayout.height
              })

              // Also track the actual .flowbuilder-node element for heat map
              const flowbuilderNode =
                runTrackComponent.querySelector<HTMLElement>(
                  '.flowbuilder-node'
                )
              if (flowbuilderNode) {
                const heatMapLayout = calculateLayout(flowbuilderNode)
                heatMapPositions.set(nodeId, {
                  x: heatMapLayout.x,
                  y: heatMapLayout.y,
                  width: heatMapLayout.width,
                  height: heatMapLayout.height
                })
              }
            }
          }
          addConnection({
            from: {
              x: runTrackComponentLayout.centerX,
              y: runTrackComponentLayout.y
            },
            to: { x: nodeLayout.centerX, y: nodeLayout.y }
          })

          const splitContainer = node.querySelector<HTMLElement>(
            '.flowbuilder-splits'
          )

          if (splitContainer) {
            const splitContainerLayout = calculateLayout(splitContainer)
            const splits = Array.from(splitContainer.children) as HTMLElement[]

            if (splits.length > 0) {
              splits.forEach((split, index: number) => {
                const splitLayout = calculateLayout(split)
                const splitLabel = split.querySelector<HTMLElement>(
                  '.flowbuilder-split-label'
                )

                if (!splitLabel) {
                  console.warn(
                    'Split label not found in a split element',
                    split
                  )
                  return
                }
                const splitLabelLayout = calculateLayout(splitLabel)

                // Track split label position
                const splitLabelId = `split-label-${index}-${Math.random()}`
                nodePositions.set(splitLabelId, {
                  x: splitLabelLayout.x,
                  y: splitLabelLayout.y,
                  width: splitLabelLayout.width,
                  height: splitLabelLayout.height
                })

                if (!terminated) {
                  addConnection({
                    from: {
                      x: splitContainerLayout.centerX,
                      y: splitContainerLayout.y,
                      el: splitContainer
                    },
                    to: {
                      x: splitLabelLayout.centerX,
                      y: splitLabelLayout.y,
                      el: splitLabel
                    },
                    type: splitContainer.classList.contains(
                      'flowbuilder-splits-linear'
                    )
                      ? 'linear'
                      : 'curved-start'
                  })

                  if (
                    splitContainer.classList.contains(
                      'flowbuilder-splits-linear'
                    )
                  ) {
                    const twoThirdsY =
                      splitContainerLayout.y +
                      ((splitLabelLayout.y - splitContainerLayout.y) * 2) / 3
                    addConnection({
                      from: {
                        x: splitLabelLayout.centerX,
                        y: twoThirdsY
                      },
                      to: {
                        x: splitLabelLayout.centerX,
                        y: splitLabelLayout.y + splitLabelLayout.height
                      },
                      type: 'linear'
                    })
                  }
                }

                // Use direct child selector to avoid finding nested run-tracks
                const runTrackInSplit =
                  split.querySelector<HTMLElement>(':scope > .run-track')
                // Get only direct children, not deeply nested items
                // This prevents cross-contamination between sibling branches
                // when checking for termination (matching pattern at lines 221-222)
                const runTrackItemsInSplit = runTrackInSplit
                  ? (Array.from(runTrackInSplit.children).filter((e) =>
                      e.classList.contains('run-track-item')
                    ) as HTMLElement[])
                  : []

                if (runTrackInSplit && runTrackItemsInSplit.length > 0) {
                  const runTrackInSplitLayout = calculateLayout(runTrackInSplit)

                  // Track nodes in the split branch
                  runTrackItemsInSplit.forEach((splitItem) => {
                    const splitNode = splitItem.querySelector<HTMLElement>(
                      '.flowbuilder-node-component'
                    )
                    if (splitNode) {
                      const splitNodeElement =
                        splitNode.querySelector('[data-node-id]')
                      if (splitNodeElement) {
                        const splitNodeId =
                          splitNodeElement.getAttribute('data-node-id')
                        if (splitNodeId) {
                          const splitNodeLayout = calculateLayout(splitNode)
                          nodePositions.set(splitNodeId, {
                            x: splitNodeLayout.x,
                            y: splitNodeLayout.y,
                            width: splitNodeLayout.width,
                            height: splitNodeLayout.height
                          })

                          // Also track the actual .flowbuilder-node element for heat map in splits
                          const flowbuilderNode =
                            splitItem.querySelector<HTMLElement>(
                              '.flowbuilder-node'
                            )
                          if (flowbuilderNode) {
                            const heatMapLayout =
                              calculateLayout(flowbuilderNode)
                            heatMapPositions.set(splitNodeId, {
                              x: heatMapLayout.x,
                              y: heatMapLayout.y,
                              width: heatMapLayout.width,
                              height: heatMapLayout.height
                            })
                          }
                        }
                      }
                    }
                  })

                  // Check if the branch terminates using NodeRun's computed attribute
                  // (avoids false positives from nested .does-terminate in sub-branches)
                  const branchTerminates =
                    runTrackInSplit?.getAttribute('data-branch-terminates') === 'true'

                  if (!branchTerminates) {
                    addConnection({
                      from: {
                        x: runTrackInSplitLayout.centerX,
                        y:
                          runTrackInSplitLayout.y + runTrackInSplitLayout.height
                      },
                      to: {
                        x: splitLayout.centerX,
                        y: splitLayout.y + splitLayout.height
                      }
                    })
                  }
                } else {
                  addConnection({
                    from: {
                      x: splitLabelLayout.centerX,
                      y: splitLabelLayout.y + splitLabelLayout.height
                    },
                    to: {
                      x: splitLayout.centerX,
                      y: splitLayout.y + splitLayout.height
                    }
                  })
                }

                if (!terminated && splitFlowsThrough(split)) {
                  // Draw convergence line from split bottom back to center,
                  // extending all the way to the runTrackComponent bottom
                  // so it connects seamlessly with the next element's connection
                  addConnection({
                    from: {
                      x: splitLayout.centerX,
                      y: splitLayout.y + splitLayout.height
                    },
                    to: {
                      x: runTrackComponentLayout.centerX,
                      y: runTrackComponentLayout.y + runTrackComponentLayout.height
                    },
                    type: splitContainer.classList.contains(
                      'flowbuilder-splits-linear'
                    )
                      ? 'linear'
                      : 'curved-end'
                  })
                }
              })
            }
          }

          // if (nodeTerminates(node)) {
          //   terminated = true
          // }
        }
      })

      const lastChild = runTrack.children[runTrack.children.length - 1] as
        | HTMLElement
        | undefined
      if (lastChild && lastChild.classList.contains('flowbuilder-dropzone')) {
        const lastChildLayout = calculateLayout(lastChild)
        addConnection({
          from: {
            x: lastChildLayout.centerX,
            y: lastChildLayout.y
          },
          to: {
            x: lastChildLayout.centerX,
            y: lastChildLayout.y + lastChildLayout.height
          }
        })
      }
    })

    connections.value = connectionPaths
    nodeElementPositions.value = nodePositions
    heatMapNodePositions.value = heatMapPositions
  }

  return {
    connections,
    edges,
    drawConnections,
    nodeElementPositions,
    heatMapNodePositions
  }
}